/**
 * Platform-resolved chrome for the standalone auth pages (reset password,
 * update password).
 *
 * `AuthRouteLayout` already swaps the SHELL per platform, but these pages'
 * content kept the web classes on every platform - a brand-purple pill and a
 * white card input sitting on the native entry paper, one screen after the
 * sign-in sheet showed an ink pill on that same paper. The chrome map extends
 * the shell swap to the content: web keeps the `AUTH_*` classes untouched,
 * native wears the sheet's own primitives, and a page consumes one map so its
 * roles cannot mix platforms.
 *
 */

import {
  AUTH_BUTTON_CLASS,
  AUTH_DESCRIPTION_CLASS,
  AUTH_HEADING_CLASS,
  AUTH_INPUT_CLASS,
  AUTH_LABEL_CLASS,
  AUTH_LINK_CLASS,
  AUTH_PASSWORD_INPUT_CLASS,
} from '@/components/auth/auth-styles';
import {
  authPrimaryOptionClass,
  entryFieldClass,
  entryLabelClass,
} from '@/components/brand/entry-styles';
import { isNativePlatform } from '@/lib/mobile/platform';

export interface AuthPageChrome {
  readonly heading: string;
  readonly description: string;
  readonly label: string;
  readonly input: string;
  readonly passwordInput: string;
  readonly primaryButton: string;
  readonly link: string;
  readonly mutedText: string;
  /** An emphasised value inside body copy (the address a link was sent to). */
  readonly emphasis: string;
  /** The password-visibility eye, tinted for the surface it sits on. */
  readonly revealIcon: string;
  /**
   * One cell of the 6-digit step-up input. Empty on web so the OTP primitive
   * keeps its own defaults untouched: the role exists to lift the cells off the
   * entry paper, which is a native-only problem.
   */
  readonly codeCell: string;
  /** The quiet full-width action sitting under `primaryButton`. */
  readonly secondaryButton: string;
  /**
   * An emphasised value inside `mutedText`, not inside `description`. It is its
   * own role because the surrounding copy is already dimmed: reusing
   * `emphasis` here would read as the brighter body-copy weight and flatten the
   * distinction the muted line depends on.
   */
  readonly mutedEmphasis: string;
}

const WEB_CHROME: AuthPageChrome = {
  heading: AUTH_HEADING_CLASS,
  description: AUTH_DESCRIPTION_CLASS,
  label: AUTH_LABEL_CLASS,
  input: AUTH_INPUT_CLASS,
  passwordInput: AUTH_PASSWORD_INPUT_CLASS,
  primaryButton: AUTH_BUTTON_CLASS,
  link: AUTH_LINK_CLASS,
  mutedText: 'text-xs text-landing-fg/30',
  emphasis: 'font-medium text-landing-fg',
  revealIcon: 'text-landing-fg/25',
  codeCell: '',
  secondaryButton: 'text-landing-fg/45 hover:bg-transparent hover:text-landing-fg/70',
  mutedEmphasis: 'font-medium text-landing-fg/60',
};

/** The sign-in sheet's own vocabulary: display heading, ink pill, entry field. */
const NATIVE_CHROME: AuthPageChrome = {
  heading: 'text-display-xs leading-8 font-semibold tracking-normal text-entry-ink',
  description: 'mt-2 text-md text-entry-ink/70',
  label: entryLabelClass,
  input: entryFieldClass,
  passwordInput: `${entryFieldClass} pr-10`,
  primaryButton: authPrimaryOptionClass,
  link: 'h-auto p-0 text-sm font-medium text-entry-ink/60 underline underline-offset-2 hover:text-entry-ink',
  mutedText: 'text-sm text-entry-ink/50',
  emphasis: 'font-medium text-entry-ink',
  revealIcon: 'text-entry-ink/40',
  // `rounded-xl` resolves to 12px here: `index.css` re-declares only `--radius`
  // and its sm/md/lg derivatives, so `--radius-xl` stays the framework default.
  // The standalone verify route's native step-up lands on that same step, so the
  // two 6-digit inputs cannot read as two different controls.
  codeCell:
    'h-13 w-11 border-entry-ink/15 bg-entry-canvas text-xl font-semibold text-entry-ink ring-entry-ink ring-offset-0 first:rounded-l-xl last:rounded-r-xl',
  // Keeps the control's own height rather than collapsing to `link`'s `h-auto
  // p-0`: this is a full-width action on a touch surface, and a text-height tap
  // target is one the thumb misses.
  secondaryButton:
    'text-entry-ink/60 underline underline-offset-2 hover:bg-transparent hover:text-entry-ink',
  mutedEmphasis: 'font-medium text-entry-ink',
};

/**
 * The sheet's round chrome affordance (its close button), reused as the native
 * back control so leaving these pages feels like leaving the sheet.
 */
export const NATIVE_BACK_BUTTON_CLASS =
  'flex size-11 items-center justify-center rounded-full bg-entry-ink/5 text-entry-ink transition-colors hover:bg-entry-ink/10 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-entry-ink';

/**
 * The web text back link, shared by every standalone auth page that carries one.
 *
 * The third member of the same set: native wears `NATIVE_BACK_BUTTON_CLASS`,
 * desktop wears `entryBackLinkClass`, web wears this. Each is a WHOLE class
 * string rather than a base plus overrides — they disagree on size and colour,
 * and resolving that by class order would rest on tailwind-merge grouping
 * project-specific colour names it has no config for.
 */
export const WEB_BACK_LINK_CLASS =
  'mb-4 h-auto p-0 text-xs font-medium text-landing-fg/45 hover:bg-transparent hover:text-landing-fg/70';

/** Resolved per render, not at module scope, so tests can vary the platform. */
export function getAuthPageChrome(): AuthPageChrome {
  return isNativePlatform() ? NATIVE_CHROME : WEB_CHROME;
}
